import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  Users,
  Search,
  Plus,
  Eye,
  Edit,
  Trash2,
  Filter,
  X,
  UserCheck,
  UserX,
  Mail,
  Briefcase,
  Building2,
  Shield,
  Phone,
  MapPin
} from 'lucide-react';
import api from '../../services/api';
import { formatDate, formatTimeAgo } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';

const UserManagement = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  // Check if current user is ADC with subdivision restriction
  const isAdcWithSubdivision = currentUser?.role === 'adc' && currentUser?.subdivisionId;

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states for create/edit
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    mobile_number: '',
    role: 'bo',
    designation: '',
    department: '',
    subdivisionId: '',
    isActive: true
  });

  // Fetch subdivisions
  const { data: subdivisionsData } = useQuery('subdivisions', async () => {
    const response = await api.get('/subdivisions');
    return response.data;
  });

  // Fetch users with pagination and filters
  const { data: usersData, isLoading } = useQuery(
    ['users', currentPage, searchTerm, roleFilter],
    async () => {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10
      });
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) params.append('role', roleFilter);

      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    },
    {
      keepPreviousData: true
    }
  );

  // Create user mutation
  const createMutation = useMutation(
    async (userData) => {
      const response = await api.post('/users', userData);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('users');
        toast.success(data.message || 'User created successfully and credentials sent via SMS!');
        setShowCreateModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create user');
      }
    }
  );

  // Update user mutation
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('User updated successfully!');
        setShowEditModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user');
      }
    }
  );

  // Delete (deactivate) user mutation
  const deleteMutation = useMutation(
    async (userId) => {
      const response = await api.delete(`/users/${userId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('User deactivated successfully!');
        setShowDeleteDialog(false);
        setSelectedUser(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to deactivate user');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      fullName: '',
      mobile_number: '',
      role: 'bo',
      designation: '',
      department: '',
      // Auto-set subdivisionId for ADC users
      subdivisionId: isAdcWithSubdivision ? currentUser.subdivisionId : '',
      isActive: true
    });
    setSelectedUser(null);
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      fullName: user.fullName,
      mobile_number: user.mobile_number || '',
      role: user.role,
      designation: user.designation || '',
      department: user.department || '',
      subdivisionId: user.subdivisionId || '',
      isActive: user.isActive
    });
    setShowEditModal(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const updateData = { ...formData };
    if (!updateData.password) {
      delete updateData.password; // Don't send empty password
    }
    delete updateData.username; // Username cannot be changed
    updateMutation.mutate({ id: selectedUser.id, data: updateData });
  };

  const getRoleBadge = (role) => {
    const badges = {
      dc: { color: 'bg-purple-100 text-purple-800', label: 'DC' },
      adc: { color: 'bg-indigo-100 text-indigo-800', label: 'ADC' },
      sdo: { color: 'bg-blue-100 text-blue-800', label: 'SDO' },
      eac: { color: 'bg-cyan-100 text-cyan-800', label: 'EAC' },
      bo: { color: 'bg-green-100 text-green-800', label: 'Branch Officer' }
    };
    const badge = badges[role] || badges.bo;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="flex items-center text-green-600">
        <UserCheck className="h-4 w-4 mr-1" />
        Active
      </span>
    ) : (
      <span className="flex items-center text-red-600">
        <UserX className="h-4 w-4 mr-1" />
        Inactive
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">User Management</h1>
          <p className="text-secondary-600 mt-1">Manage system users and their permissions</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 pr-4 py-2 w-full border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Role Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 h-5 w-5" />
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 pr-4 py-2 w-full border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Roles</option>
              <option value="dc">DC</option>
              <option value="adc">ADC</option>
              <option value="sdo">SDO</option>
              <option value="eac">EAC</option>
              <option value="bo">Branch Officer</option>
            </select>
          </div>

          {/* Clear Filters */}
          {(searchTerm || roleFilter) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setRoleFilter('');
                setCurrentPage(1);
              }}
              className="btn btn-outline"
            >
              <X className="h-4 w-4 mr-2" />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : usersData?.users?.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900">No users found</h3>
            <p className="mt-1 text-sm text-secondary-500">
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Subdivision
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {usersData?.users?.map((user) => (
                    <tr key={user.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-600 font-medium text-sm">
                                {user.fullName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-secondary-900">
                              {user.fullName}
                            </div>
                            <div className="text-sm text-secondary-500">@{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.subdivision ? (
                          <div className="text-sm">
                            <div className="text-secondary-900">{user.subdivision.name}</div>
                            <div className="text-secondary-500 text-xs">
                              {user.subdivision.code}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-secondary-400 italic">
                            {user.role === 'dc' ? 'District-wide' : 'Not assigned'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-secondary-900">
                          {user.designation || '-'}
                        </div>
                        <div className="text-sm text-secondary-500">
                          {user.department || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">{getStatusBadge(user.isActive)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleView(user)}
                          className="text-primary-600 hover:text-primary-900 mr-3"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title="Edit User"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="text-red-600 hover:text-red-900"
                          title="Deactivate User"
                          disabled={!user.isActive}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {usersData?.pagination && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-secondary-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!usersData.pagination.hasPrev}
                    className="btn btn-outline"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!usersData.pagination.hasNext}
                    className="btn btn-outline ml-3"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-secondary-700">
                      Showing page{' '}
                      <span className="font-medium">{usersData.pagination.currentPage}</span> of{' '}
                      <span className="font-medium">{usersData.pagination.totalPages}</span>
                      {' '}({usersData.pagination.totalUsers} total users)
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={!usersData.pagination.hasPrev}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={!usersData.pagination.hasNext}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-secondary-900">User Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-secondary-400 hover:text-secondary-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-6">
                {/* User Avatar and Name */}
                <div className="flex items-center space-x-4">
                  <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-600 font-medium text-2xl">
                      {selectedUser.fullName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-secondary-900">
                      {selectedUser.fullName}
                    </h4>
                    <p className="text-secondary-500">@{selectedUser.username}</p>
                  </div>
                </div>

                {/* User Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      <Mail className="h-4 w-4 inline mr-1" />
                      Email
                    </label>
                    <p className="text-sm text-secondary-900">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      <Phone className="h-4 w-4 inline mr-1" />
                      Mobile Number
                    </label>
                    <p className="text-sm text-secondary-900">
                      {selectedUser.mobile_number || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      <Shield className="h-4 w-4 inline mr-1" />
                      Role
                    </label>
                    <div>{getRoleBadge(selectedUser.role)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      <MapPin className="h-4 w-4 inline mr-1" />
                      Subdivision
                    </label>
                    {selectedUser.subdivision ? (
                      <p className="text-sm text-secondary-900">
                        {selectedUser.subdivision.name} ({selectedUser.subdivision.code})
                      </p>
                    ) : (
                      <p className="text-sm text-secondary-500 italic">
                        {selectedUser.role === 'dc' ? 'District-wide access' : 'Not assigned'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      <Briefcase className="h-4 w-4 inline mr-1" />
                      Designation
                    </label>
                    <p className="text-sm text-secondary-900">
                      {selectedUser.designation || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      <Building2 className="h-4 w-4 inline mr-1" />
                      Department
                    </label>
                    <p className="text-sm text-secondary-900">
                      {selectedUser.department || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      Status
                    </label>
                    <div className="text-sm">{getStatusBadge(selectedUser.isActive)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      Last Login
                    </label>
                    <p className="text-sm text-secondary-900">
                      {selectedUser.lastLogin
                        ? formatTimeAgo(selectedUser.lastLogin)
                        : 'Never'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      Created At
                    </label>
                    <p className="text-sm text-secondary-900">
                      {formatDate(selectedUser.createdAt)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 mb-1">
                      Updated At
                    </label>
                    <p className="text-sm text-secondary-900">
                      {formatDate(selectedUser.updatedAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-secondary-200 flex justify-end space-x-3">
              <button onClick={() => setShowViewModal(false)} className="btn btn-outline">
                Close
              </button>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(selectedUser);
                }}
                className="btn btn-primary"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-secondary-900">Add New User</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-secondary-400 hover:text-secondary-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Username <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.username}
                      onChange={(e) =>
                        setFormData({ ...formData, username: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="username"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="user@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.fullName}
                      onChange={(e) =>
                        setFormData({ ...formData, fullName: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Full Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      pattern="[0-9]{10,15}"
                      maxLength="15"
                      value={formData.mobile_number}
                      onChange={(e) =>
                        setFormData({ ...formData, mobile_number: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="9876543210"
                    />
                    <p className="text-xs text-secondary-500 mt-1">
                      A random 12-character password will be generated and sent via SMS
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="bo">Branch Officer</option>
                      <option value="eac">EAC</option>
                      <option value="sdo">SDO</option>
                      {/* ADC and DC options only visible to DC users */}
                      {currentUser?.role === 'dc' && (
                        <>
                          <option value="adc">ADC</option>
                          <option value="dc">DC</option>
                        </>
                      )}
                    </select>
                    {isAdcWithSubdivision && (
                      <p className="mt-1 text-xs text-blue-600">
                        As ADC, you can only create BO, EAC, and SDO users in your subdivision.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      value={formData.designation}
                      onChange={(e) =>
                        setFormData({ ...formData, designation: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Senior Officer"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) =>
                      setFormData({ ...formData, department: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., Land Administration"
                  />
                </div>

                {/* Subdivision dropdown - required for BO/EAC/SDO/ADC */}
                {['bo', 'eac', 'sdo', 'adc'].includes(formData.role) && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Subdivision <span className="text-red-500">*</span>
                    </label>
                    {isAdcWithSubdivision ? (
                      // ADC users see their subdivision locked (auto-selected)
                      <>
                        <input
                          type="text"
                          value={subdivisionsData?.subdivisions?.find(s => s.id === currentUser.subdivisionId)?.name || 'Your Subdivision'}
                          disabled
                          className="w-full px-3 py-2 border border-secondary-300 rounded-lg bg-secondary-100 text-secondary-700 cursor-not-allowed"
                        />
                        <p className="text-xs text-blue-600 mt-1">
                          Users will be created in your subdivision (ADC restriction)
                        </p>
                      </>
                    ) : (
                      // DC users can select any subdivision
                      <>
                        <select
                          required
                          value={formData.subdivisionId}
                          onChange={(e) => setFormData({ ...formData, subdivisionId: e.target.value })}
                          className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Select Subdivision</option>
                          {subdivisionsData?.subdivisions?.map((subdivision) => (
                            <option key={subdivision.id} value={subdivision.id}>
                              {subdivision.name} ({subdivision.code})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-secondary-500 mt-1">
                          {formData.role.toUpperCase()} users must be assigned to a subdivision
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* District-wide info for DC only */}
                {formData.role === 'dc' && currentUser?.role === 'dc' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>DC</strong> has district-wide access to all subdivisions (cannot be assigned to a specific subdivision)
                    </p>
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-secondary-900">
                    Active user
                  </label>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-secondary-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="btn btn-outline"
                  disabled={createMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isLoading}
                >
                  {createMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-secondary-900">Edit User</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-secondary-400 hover:text-secondary-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      disabled
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg bg-secondary-50 text-secondary-500 cursor-not-allowed"
                    />
                    <p className="text-xs text-secondary-500 mt-1">Username cannot be changed</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Leave blank to keep current"
                    />
                    <p className="text-xs text-secondary-500 mt-1">
                      Only enter if you want to change the password
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.fullName}
                      onChange={(e) =>
                        setFormData({ ...formData, fullName: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      pattern="[0-9]{10,15}"
                      maxLength="15"
                      value={formData.mobile_number}
                      onChange={(e) =>
                        setFormData({ ...formData, mobile_number: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="9876543210"
                    />
                    <p className="text-xs text-secondary-500 mt-1">
                      Enter 10-15 digit mobile number
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={isAdcWithSubdivision && ['adc', 'dc'].includes(selectedUser?.role)}
                    >
                      <option value="bo">Branch Officer</option>
                      <option value="eac">EAC</option>
                      <option value="sdo">SDO</option>
                      {/* ADC and DC options only visible to DC users */}
                      {currentUser?.role === 'dc' && (
                        <>
                          <option value="adc">ADC</option>
                          <option value="dc">DC</option>
                        </>
                      )}
                    </select>
                    {isAdcWithSubdivision && (
                      <p className="mt-1 text-xs text-blue-600">
                        As ADC, you can only edit BO, EAC, and SDO users in your subdivision.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      value={formData.designation}
                      onChange={(e) =>
                        setFormData({ ...formData, designation: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) =>
                      setFormData({ ...formData, department: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Subdivision dropdown - required for BO/EAC/SDO/ADC */}
                {['bo', 'eac', 'sdo', 'adc'].includes(formData.role) && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Subdivision <span className="text-red-500">*</span>
                    </label>
                    {isAdcWithSubdivision && ['bo', 'eac', 'sdo'].includes(formData.role) ? (
                      // ADC users editing BO/EAC/SDO see their subdivision locked
                      <>
                        <input
                          type="text"
                          value={subdivisionsData?.subdivisions?.find(s => s.id === currentUser.subdivisionId)?.name || 'Your Subdivision'}
                          disabled
                          className="w-full px-3 py-2 border border-secondary-300 rounded-lg bg-secondary-100 text-secondary-700 cursor-not-allowed"
                        />
                        <p className="text-xs text-blue-600 mt-1">
                          Subdivision cannot be changed (ADC restriction)
                        </p>
                      </>
                    ) : (
                      // DC users can select any subdivision
                      <>
                        <select
                          required
                          value={formData.subdivisionId}
                          onChange={(e) => setFormData({ ...formData, subdivisionId: e.target.value })}
                          className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Select Subdivision</option>
                          {subdivisionsData?.subdivisions?.map((subdivision) => (
                            <option key={subdivision.id} value={subdivision.id}>
                              {subdivision.name} ({subdivision.code})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-secondary-500 mt-1">
                          {formData.role.toUpperCase()} users must be assigned to a subdivision
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* District-wide info for DC only */}
                {formData.role === 'dc' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>DC</strong> has district-wide access to all subdivisions (cannot be assigned to a specific subdivision)
                    </p>
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActiveEdit"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  />
                  <label
                    htmlFor="isActiveEdit"
                    className="ml-2 block text-sm text-secondary-900"
                  >
                    Active user
                  </label>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-secondary-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="btn btn-outline"
                  disabled={updateMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateMutation.isLoading}
                >
                  {updateMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Edit className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">
                Deactivate User
              </h3>
              <p className="text-secondary-600 mb-4">
                Are you sure you want to deactivate user <strong>{selectedUser.fullName}</strong>?
                This will prevent them from logging in to the system.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setSelectedUser(null);
                  }}
                  className="btn btn-outline"
                  disabled={deleteMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate(selectedUser.id)}
                  className="btn bg-red-600 text-white hover:bg-red-700"
                  disabled={deleteMutation.isLoading}
                >
                  {deleteMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deactivating...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Deactivate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
